import ImplicitLogSchemas from '../config/ImplicitLogSchemas';
import CommonParameter from '../parameters/CommonParameter';
import ParameterStore from '../parameters/ParameterStore';
import LogRequester from '../requesters/LogRequester';
import ConsoleLog from '../utils/ConsoleLog';
import UUID from '../utils/UUID';
import EventUtil from '../utils/EventUtil';
import RequestUtil from '../utils/RequestUtil';

type TrackedElementsType = {
  [key: string]: Array<StatusTimestamp>;
};

type StatusTimestamp = {
  isVisible: boolean;
  ts: number;
};

const TAG = 'Impression';

// Minimum time the element should be visible for (millisecond)
const DEFAULT_TIME_THRESHOLD = 1000;

// Minimum portion of the element that should be visible
const DEFAULT_AREA_THRESHOLD = 0.2;

const TRACK_ID_SELECTOR = '[data-web-log-track-id]';
const IMPRESSION_SELECTOR = `[data-widget-name]:not(${TRACK_ID_SELECTOR}),[data-web-log-event='impression']:not(${TRACK_ID_SELECTOR})`;
const EXCLUDE_SELECTOR = `[data-web-log-exclude='impression']`;

export default class Impression {
  commonData: CommonDataType;
  commonParameter: CommonParameter;
  parameterStore: ParameterStore;
  campaignParams: CampaignParamType;
  logRequester: LogRequester;
  areaThreshold: number;
  timeThreshold: number;
  trackedElements: TrackedElementsType;

  constructor(parameterStore: ParameterStore, commonParameter: CommonParameter, commonData: CommonDataType, campaignParams: CampaignParamType, logRequester: LogRequester, options: ImpressionOptions) {
    this.commonData = commonData;
    this.commonParameter = commonParameter;
    this.parameterStore = parameterStore;
    this.campaignParams = campaignParams;
    this.logRequester = logRequester;

    // To store elements and their visible state with timestamp
    this.trackedElements = {};

    this.areaThreshold = options?.areaThreshold || DEFAULT_AREA_THRESHOLD;
    this.timeThreshold = options?.timeThreshold || DEFAULT_TIME_THRESHOLD;
  }

  /**
   * Initialize IntersectionObserver for one element if the param is given,
   * or all elements with certain data attribute.
   * @param elem {HTMLElement}
   */
  initTracking(elem?: HTMLElement): void {
    const options = {
      threshold: this.areaThreshold
    };

    const intersectObserver = new IntersectionObserver(this.trackStatus.bind(this), options);

    // Set data-web-log-track-id to use it as the unique id for each element.
    const setTrackId = (element: HTMLElement): void => {
      if (element === undefined || element.dataset?.webLogTrackId) {
        return;
      }
      if (element.dataset) {
        element.dataset.webLogTrackId = UUID.generate();
        this.trackedElements[element.dataset.webLogTrackId] = [];
      }
    };

    // Callback for MutationObserver for the element to be tracked.
    // If the element's contents are refreshed, we need to track impression again.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const observeRefreshedElements = (records: MutationRecord[], observer: MutationObserver): void => {
      for (const record of records) {
        const el = record.target as HTMLElement;
        const target = el.closest(TRACK_ID_SELECTOR);
        if (target) {
          intersectObserver.observe(target);
        }
      }
    };

    const setMutationObserverForElem = (elem: HTMLElement): void => {
      const mutationObserver = new MutationObserver(observeRefreshedElements);
      mutationObserver.observe(elem, { childList: true, subtree: true });
    };

    // In case the consumer needs to track impression for a specific element
    if (elem) {
      setTrackId(elem);
      intersectObserver.observe(elem);
    } else {
      const observeElements = (): void => {
        const elems = document.body.querySelectorAll(IMPRESSION_SELECTOR);
        elems.forEach((el: HTMLElement) => {
          if (!el.matches(EXCLUDE_SELECTOR)) {
            setTrackId(el);
            intersectObserver.observe(el);

            // For elements that can be refreshed.
            setMutationObserverForElem(el);
          }
        });
      };

      // For ssr contents
      observeElements();

      // Use MutationObserver to observe the csr modules after the page load.
      const mutationObserver = new MutationObserver(observeElements);
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    // We want to check if the element has been visible longer than the timeThreshold with an interval.
    const intervalID = window.setInterval(() => {
      this.trackImpression(intersectObserver);
    }, DEFAULT_TIME_THRESHOLD);

    // We want to track impression right before the user leaves the page as well.
    window.onbeforeunload = () => {
      this.trackImpression(intersectObserver);
      if (intervalID) {
        clearInterval(intervalID);
      }
    };
  }

  /**
   * Callback for IntersectionObserver
   * @param entries {Array<IntersectionObserverEntry>}
   * @param observer {IntersectionObserver}
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackStatus(entries: Array<IntersectionObserverEntry>, observer: IntersectionObserver): void {
    entries.forEach(entry => {
      const target = entry.target as HTMLElement;

      if (!target.matches(EXCLUDE_SELECTOR) && entry.isIntersecting && EventUtil.isVisible(target)) {
        const id = target.dataset?.webLogTrackId;
        const statusTimestamps = this.trackedElements[id] ? this.trackedElements[id] : [];

        statusTimestamps.push({
          isVisible: entry.isIntersecting,
          ts: entry.time
        });
        this.trackedElements[id] = statusTimestamps;
      }
    });
  }

  /**
   * Track an element's visible state and time and trigger sendImpressionLog.
   * @param observer {IntersectionObserver}
   */
  trackImpression(observer: IntersectionObserver) {
    Object.keys(this.trackedElements).forEach(id => {
      if (EventUtil.isVisibleLongerThanMinTime(this.trackedElements[id], this.timeThreshold)) {
        const target = document.querySelector(`[data-web-log-track-id="${id}"]`) as HTMLElement;

        if (target) {
          this.sendImpressionLog(target);

          delete this.trackedElements[id];

          target.setAttribute('data-web-log-track-id', 'done');

          // Stop observing this target to send the impression event only once.
          observer.unobserve(target);
        }
      }
    }, this);
  }

  /**
   * Generate the RequestParam data and send it to the collector.
   * @param target {HTMLElement}
   */
  sendImpressionLog(target: HTMLElement, customData: RequestParams = {}) {
    if (target === undefined) {
      return;
    }

    const eventparams = RequestUtil.generateEventData('impression', target, this.parameterStore, this.commonData);

    const params = Object.keys(customData).length > 0 ? RequestUtil.mergeRequestParams(eventparams, customData) : eventparams;
    const updatedParams = RequestUtil.generateImplicitLogRequestParams(ImplicitLogSchemas.Impression, params, this.commonData);
    const requestJSON = RequestUtil.generateRequestJSON(updatedParams, this.commonParameter, this.campaignParams);
    this.logRequester.send(requestJSON, true);

    ConsoleLog.d(TAG, `Send impression: ${target.dataset?.widgetName || target.dataset?.componentName}`);
  }
}
